import { v4 as uuidv4 } from 'uuid';

import { type PresentationContentBlockBase, type PresentationContentBlockOutput } from '@air/api/types';

export type ShowcaseContentBlockType = PresentationContentBlockOutput['type'];

/**
 * The fields every page-body block carries, whichever side it comes from: the wire's
 * `PresentationContentBlockOutput` union and ShortIdProvider's read-side `presentationContent`
 * blocks both satisfy it. Chrome that only needs identity/visibility/type (the edit canvas shell,
 * header, and menus) types against this so it accepts blocks from either source.
 */
export type ShowcaseContentBlockBase = PresentationContentBlockBase & {
  type: ShowcaseContentBlockType;
};

/**
 * Product labels for the share link's page-body block types — shared by the editor's Content tab
 * rows and the edit canvas block chrome so the two authoring surfaces can never drift apart.
 */
export const SHOWCASE_CONTENT_BLOCK_LABELS: Record<ShowcaseContentBlockType, string> = {
  assets: 'Assets',
  boards: 'Boards',
  files: 'Files',
  image: 'Image',
  links: 'Links',
  text: 'Text',
  title: 'Title',
  video: 'Video',
};

export type ShowcaseListingBlockType = Extract<ShowcaseContentBlockType, 'assets' | 'boards' | 'files'>;

/**
 * Listing blocks are the collection's own item sections — they can be hidden or reordered but
 * never removed from the page (product rule), so no authoring surface offers a delete affordance
 * for them. The type predicate narrows `type` so render call sites can pass it on directly.
 */
export const isListingContentBlock = <Block extends ShowcaseContentBlockBase>(
  block: Block,
): block is Block & { type: ShowcaseListingBlockType } =>
  block.type === 'assets' || block.type === 'boards' || block.type === 'files';

/**
 * Order matches the backend's sub-page seeding order, so a link that has never authored
 * `presentationContent` shows the same page body everywhere it is rendered.
 */
export const DEFAULT_LISTING_BLOCK_TYPES = ['boards', 'assets', 'files'] as const;

/**
 * The page body of a showcase that has never authored `presentationContent`. EVERY surface renders
 * a showcase through the block stack — there is no second, flat render path any more — so this
 * array is what the read seam substitutes for absent content (see
 * `usePublicShowcaseActivePageContent`) and what the editor's Content tab lists before the first
 * edit materializes a body.
 *
 * The ids are FIXED rather than generated, and that is the whole point: the Content tab's implicit
 * rows, the public page, and the editor canvas all key their React elements on them, and the first
 * edit materializes THESE ids into the draft. A generated id would differ between the surface that
 * synthesized it and the surface that materialized it, so the listing blocks would remount — the
 * visible flip this module exists to remove. They are ordinary UUIDs so the wire shape is
 * unchanged; only their stability is special.
 */
export const SHOWCASE_IMPLICIT_DEFAULT_BLOCKS: PresentationContentBlockOutput[] = [
  { id: 'd5b5cc32-b4e9-4593-aae1-80a80370d6af', type: 'boards' },
  { id: 'e1a0107c-e489-40cb-9344-33853aacc904', type: 'assets' },
  { id: '2cc5e88c-a58c-4963-a6b3-a6270d8a4d46', type: 'files' },
];

/**
 * A FRESH default body, with generated ids — for fabricating a body that is stored as its own page.
 * The create-sub-page mutation needs this because `Collections.create` returns the collection, not
 * the overlay entry it wrote, so the client mirrors the backend's seeding locally; two sub-pages
 * created in one session must not share block ids.
 *
 * Distinct from `SHOWCASE_IMPLICIT_DEFAULT_BLOCKS` on purpose: that array is a stand-in for a body
 * that does not exist yet and must be identical everywhere, this one is a real new body.
 */
export const createDefaultListingBlocks = (): PresentationContentBlockOutput[] =>
  DEFAULT_LISTING_BLOCK_TYPES.map((type) => ({ id: uuidv4(), type }));
